import { headlineStats, riskIndex, navItems } from "@/mocks/home";

export default function HeroSection() {
  return (
    <section id="top" className="relative w-full overflow-hidden border-b border-background-200 bg-background-50">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-primary-100/60 blur-3xl"></div>
        <div className="absolute right-1/3 top-1/2 h-64 w-64 rounded-full bg-accent-100/70 blur-3xl"></div>
      </div>

      <div className="relative mx-auto max-w-[1400px] px-4 pb-10 pt-10 md:px-6 md:pb-14 md:pt-16">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-10">
          {/* text-first column */}
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-700">
              <span className="h-2 w-2 animate-pulseDot rounded-full bg-primary-500"></span>
              品牌方与市场人的代言风险排查台
            </span>

            <h2 className="mt-5 font-heading text-3xl font-black leading-[1.15] tracking-tight text-foreground-950 md:text-5xl">
              代言先排雷，
              <br className="hidden sm:block" />
              合作再谈钱。
            </h2>

            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              17黑料网把散落各处的商务风险线索整理成一张可读的观察表：从代言人翻车到品牌解约，
              从公关危机节奏到商业价值影响，帮你在官宣、续约、物料上线这些关键节点前，
              多拿到几天提前量。这里不做情绪爆料，只做<strong className="font-semibold text-foreground-900">合作避雷</strong>的参考依据。
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <a
                href="#risk-flash"
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-flashlight-line w-4 h-4 flex items-center justify-center"></i>
                查看今日风险速报
              </a>
              <a
                href="#method"
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-foreground-400"
              >
                <i className="ri-flow-chart w-4 h-4 flex items-center justify-center"></i>
                了解监控方法
              </a>
            </div>

            <dl className="mt-9 grid grid-cols-2 gap-x-4 gap-y-5 border-t border-background-200 pt-6 md:grid-cols-4">
              {headlineStats.map((s) => (
                <div key={s.label}>
                  <dd className="flex items-baseline gap-1">
                    <span className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                      {s.value}
                    </span>
                    <span className="text-xs text-foreground-500">{s.unit}</span>
                  </dd>
                  <dt className="mt-1 text-xs text-foreground-600">{s.label}</dt>
                  <span
                    className={`mt-1 inline-block text-[11px] ${
                      s.trend.startsWith("-") ? "text-secondary-600" : "text-primary-600"
                    }`}
                  >
                    {s.trend} 较昨日
                  </span>
                </div>
              ))}
            </dl>
          </div>

          {/* visual accompaniment column */}
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -right-3 -top-3 hidden h-full w-full rounded-lg border border-background-300 bg-background-100 md:block"></div>
              <div className="relative overflow-hidden rounded-lg bg-foreground-950">
                <img
                  src="https://readdy.ai/api/search-image?query=abstract%20editorial%20visual%20of%20a%20business%20risk%20monitoring%20network%2C%20deep%20crimson%20and%20warm%20amber%20nodes%20connected%20on%20a%20dark%20charcoal%20background%2C%20minimal%20studio%20light%2C%20clean%20geometric%20line%20art%2C%20sophisticated%20data%20aesthetic%2C%20high%20detail&width=1100&height=1300&seq=hero-risk-2026x&orientation=portrait"
                  alt="17黑料网代言风险监控示意图"
                  title="17黑料网代言风险监控示意图"
                  className="h-64 w-full object-top opacity-80 md:h-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/70 to-transparent"></div>

                <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-xs text-background-200">今日综合风险指数</p>
                      <div className="mt-1 flex items-baseline gap-2">
                        <span className="font-heading text-5xl font-black leading-none text-background-50">
                          {riskIndex.score}
                        </span>
                        <span className="rounded-full bg-primary-500 px-2 py-0.5 text-[11px] font-semibold text-background-50">
                          {riskIndex.level}
                        </span>
                      </div>
                    </div>
                    <span className="flex items-center gap-1 text-xs text-accent-400">
                      <i className="ri-arrow-up-line w-3.5 h-3.5 flex items-center justify-center"></i>
                      {riskIndex.delta}
                    </span>
                  </div>

                  <div className="mt-4 space-y-2">
                    {[
                      { k: "私德 / 言论类", v: 82 },
                      { k: "税务 / 合规类", v: 64 },
                      { k: "合作履约类", v: 41 },
                    ].map((row) => (
                      <div key={row.k}>
                        <div className="mb-1 flex items-center justify-between text-[11px] text-background-200">
                          <span>{row.k}</span>
                          <span>{row.v}</span>
                        </div>
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-background-50/15">
                          <div
                            className="h-full rounded-full bg-accent-500"
                            style={{ width: `${row.v}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-3 hidden items-center justify-between rounded-lg border border-background-200 bg-background-100 px-4 py-3 text-xs text-foreground-600 md:flex">
                <span className="flex items-center gap-2">
                  <i className="ri-timer-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
                  下次数据刷新 30 分钟后
                </span>
                <a href="#endorse-rank" className="whitespace-nowrap font-semibold text-primary-600 hover:underline">
                  榜单直达
                </a>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {navItems.slice(0, 4).map((n) => (
                <a
                  key={n.key}
                  href={`#${n.key}`}
                  className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  #{n.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}